import { useEffect, useRef, useState } from 'react'
import type { Call, CallTurn, Highlight } from '@/shared/api/types'
import styles from './CallPlayer.module.css'

interface CallPlayerProps {
  call: Call
  turns: CallTurn[]
  /** Моменты разговора, размеченные моделью. */
  highlights?: Highlight[]
  /** Какая реплика звучит сейчас — по ней подсвечивается стенограмма. */
  onActiveTurn?: (index: number | null) => void
  /** Позиция, на которую попросили перемотать снаружи, в секундах. */
  seekTo?: number | null
}

const SPEEDS = [1, 1.5, 2] as const

/**
 * Проигрыватель записи с разметкой разговора.
 *
 * Дорожка показывает, кто когда говорил, и где собеседник перебил
 * агента. Это главное, ради чего запись открывают: услышать не весь
 * разговор подряд, а конкретное место — где агент сказал лишнее или где
 * его оборвали.
 *
 * Запись стерео: слева собеседник, справа агент. Разметка построена по
 * стенограмме, а не по анализу звука — времена реплик уже известны
 * точно, и угадывать их по громкости незачем.
 */
export function CallPlayer({
  call,
  turns,
  highlights = [],
  onActiveTurn,
  seekTo,
}: CallPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [speed, setSpeed] = useState<number>(1)
  const [hover, setHover] = useState<{ turn: CallTurn; left: number } | null>(null)

  // Длительность берём из звонка, а не из аудио: метаданные файла
  // подгружаются не сразу, а дорожку нужно нарисовать до первого клика.
  const total = call.durationMs / 1000

  useEffect(() => {
    if (seekTo == null || !audioRef.current) return
    audioRef.current.currentTime = seekTo
    setPosition(seekTo)
  }, [seekTo])

  useEffect(() => {
    if (!onActiveTurn) return
    onActiveTurn(activeIndex(turns, position * 1000))
  }, [position, turns, onActiveTurn])

  function toggle() {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      void audio.play()
      setPlaying(true)
    } else {
      audio.pause()
      setPlaying(false)
    }
  }

  function seek(event: React.MouseEvent<HTMLDivElement>) {
    const audio = audioRef.current
    if (!audio || total <= 0) return

    const box = event.currentTarget.getBoundingClientRect()
    const share = (event.clientX - box.left) / box.width
    audio.currentTime = Math.max(0, Math.min(total, share * total))
    setPosition(audio.currentTime)
  }

  /** Перевести воспроизведение на позицию и продолжить с неё. */
  function jump(seconds: number) {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = seconds
    setPosition(seconds)
    if (audio.paused) {
      void audio.play()
      setPlaying(true)
    }
  }

  function changeSpeed(value: number) {
    setSpeed(value)
    if (audioRef.current) audioRef.current.playbackRate = value
  }

  const active = activeIndex(turns, position * 1000)

  return (
    <div className={styles.player}>
      <audio
        ref={audioRef}
        src={`/api/calls/${call.id}/audio`}
        preload="metadata"
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
        onEnded={() => setPlaying(false)}
      />

      <div
        className={styles.track}
        onClick={seek}
        onMouseLeave={() => setHover(null)}
        role="slider"
        aria-label="Перемотка записи"
        aria-valuemin={0}
        aria-valuemax={Math.round(total)}
        aria-valuenow={Math.round(position)}
        tabIndex={0}
      >
        {turns.map((turn) => {
          const left = percent(turn.offsetMs, total)
          const width = Math.max(percent(turnLength(turn, turns, total), total), 0.4)
          const human = turn.role === 'human'

          return (
            <div
              key={turn.index}
              className={[
                styles.segment,
                human ? styles.segmentHuman : styles.segmentAgent,
                turn.index === active ? styles.segmentActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ left: `${left}%`, width: `${width}%` }}
              onMouseEnter={() => setHover({ turn, left })}
            />
          )
        })}

        {/* Засечки перебиваний поверх полос: их ищут в записи чаще
            всего, и они не должны теряться среди реплик. */}
        {turns
          .filter((turn) => turn.interrupted)
          .map((turn) => (
            <div
              key={`cut-${turn.index}`}
              className={styles.interrupt}
              style={{ left: `${percent(turn.offsetMs + estimateHeard(turn), total)}%` }}
              title="здесь перебили"
            />
          ))}

        {/* Смысловые метки. Стоят поверх полос: за ними к записи и
            возвращаются, а реплики — только фон. */}
        {highlights.map((mark, i) => (
          <button
            key={`${mark.turnIndex}-${mark.kind}-${i}`}
            type="button"
            className={`${styles.mark} ${mark.good ? styles.markGood : styles.markBad}`}
            style={{ left: `${percent(mark.offsetMs, total)}%` }}
            onClick={(e) => {
              e.stopPropagation()
              jump(mark.offsetMs / 1000)
            }}
            title={`${mark.label}${mark.note ? ` — ${mark.note}` : ''}`}
            aria-label={mark.label}
          >
            <span className={styles.markLine} />
            <span className={styles.markFlag}>{mark.good ? '✓' : '!'}</span>
          </button>
        ))}

        <div className={styles.cursor} style={{ left: `${percent(position * 1000, total)}%` }} />

        {hover && (
          <span className={styles.tooltip} style={{ left: `${Math.min(hover.left, 70)}%` }}>
            {hover.turn.role === 'human' ? 'Вы: ' : 'Агент: '}
            {clip(hover.turn.text)}
          </span>
        )}
      </div>

      <div className={styles.controls}>
        <button type="button" className={styles.play} onClick={toggle} aria-label={playing ? 'Пауза' : 'Слушать'}>
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>

        <span className={styles.time}>
          {clock(position)} / {clock(total)}
        </span>

        <div className={styles.speed}>
          {SPEEDS.map((value) => (
            <button
              key={value}
              type="button"
              className={`${styles.speedButton} ${speed === value ? styles.speedActive : ''}`}
              onClick={() => changeSpeed(value)}
            >
              {value}×
            </button>
          ))}
        </div>

        <a
          className={styles.download}
          href={`/api/calls/${call.id}/audio`}
          download={`разговор-${call.id}.wav`}
          aria-label="Скачать запись"
        >
          <DownloadIcon />
        </a>
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendAgent}`} />
          агент
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendHuman}`} />
          собеседник
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendCut}`} />
          перебивание
        </span>
        <span className={styles.legendItem}>запись стерео: слева собеседник, справа агент</span>
      </div>

      {highlights.length > 0 && (
        <div className={styles.moments}>
          <span className={styles.momentsTitle}>Что произошло в разговоре</span>
          {highlights.map((mark, i) => {
            // Момент считается текущим, пока не начался следующий: так
            // при воспроизведении подсвечен ровно один.
            const next = highlights[i + 1]
            const current =
              position * 1000 >= mark.offsetMs &&
              (!next || position * 1000 < next.offsetMs)

            return (
              <button
                key={`${mark.turnIndex}-${mark.kind}-${i}`}
                type="button"
                className={`${styles.moment} ${current ? styles.momentActive : ''}`}
                onClick={() => jump(mark.offsetMs / 1000)}
              >
                <span className={styles.momentTime}>{clock(mark.offsetMs / 1000)}</span>
                <span className={styles.momentBody}>
                  <span
                    className={`${styles.momentLabel} ${
                      mark.good ? styles.momentGood : styles.momentBad
                    }`}
                  >
                    {mark.label}
                  </span>
                  {mark.note && <span className={styles.momentNote}>{mark.note}</span>}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Доля от общей длительности в процентах. */
function percent(ms: number, totalSeconds: number): number {
  if (totalSeconds <= 0) return 0
  return Math.max(0, Math.min(100, (ms / 1000 / totalSeconds) * 100))
}

/**
 * Длительность реплики.
 *
 * Считается до начала следующей: сколько именно длилась речь, мы не
 * храним, а для разметки важнее, чтобы отрезки не перекрывались и не
 * оставляли дыр.
 */
function turnLength(turn: CallTurn, turns: CallTurn[], total: number): number {
  const next = turns.find((t) => t.offsetMs > turn.offsetMs)
  const end = next ? next.offsetMs : total * 1000
  return Math.max(end - turn.offsetMs, 300)
}

/**
 * Где внутри реплики её оборвали.
 *
 * По доле услышанного текста от полного. Приблизительно — речь не
 * равномерна, — но достаточно, чтобы засечка стояла в нужной трети, а
 * не в начале реплики.
 */
function estimateHeard(turn: CallTurn): number {
  if (!turn.heard || !turn.text) return 0
  const share = turn.heard.length / turn.text.length
  return share * 3000
}

function activeIndex(turns: CallTurn[], positionMs: number): number | null {
  let found: number | null = null
  for (const turn of turns) {
    if (turn.offsetMs <= positionMs) found = turn.index
    else break
  }
  return found
}

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const mm = Math.floor(seconds / 60)
  const ss = Math.floor(seconds % 60)
  return `${mm}:${String(ss).padStart(2, '0')}`
}

function clip(text: string): string {
  return text.length > 60 ? `${text.slice(0, 60)}…` : text
}

function PlayIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 16 18" fill="currentColor" aria-hidden="true">
      <path d="M2 1.8C2 1 2.9 0.5 3.6 0.9L14.4 8.1C15 8.5 15 9.5 14.4 9.9L3.6 17.1C2.9 17.5 2 17 2 16.2V1.8Z" />
    </svg>
  )
}

function PauseIcon() {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor" aria-hidden="true">
      <rect x="0" y="0" width="5" height="16" rx="1.5" />
      <rect x="9" y="0" width="5" height="16" rx="1.5" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M9 2V11.5M9 11.5L5.5 8M9 11.5L12.5 8M3 14.5H15"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
